import { formatDistanceToNowStrict } from "date-fns";

// Same threshold as the backend's "check failing" Slack alert: 10 failed
// checks in a row (checks run every 30s, so about 5 minutes). A single
// failed check does not change what people see.
export const CHECK_FAILING_AFTER = 10;

// An admin switched the check off: the status is not monitored at all
export const isChecksPaused = (exchangeStatus) =>
  !!exchangeStatus?.checksPausedAt;

// The status check itself is broken: the deposit/withdrawal status is the
// last known one and may be outdated.
export const isCheckFailing = (exchangeStatus) =>
  !!exchangeStatus &&
  !isChecksPaused(exchangeStatus) &&
  (exchangeStatus.checkFailingAlertSent === true ||
    (exchangeStatus.checkErrorCount ?? 0) >= CHECK_FAILING_AFTER);

// "online" | "offline" | "check failing" | "paused" | "no data"
export const statusLabel = (exchangeStatus, type) => {
  if (!exchangeStatus) return "no data";
  if (isChecksPaused(exchangeStatus)) return "paused";
  if (isCheckFailing(exchangeStatus)) return "check failing";
  const allowed =
    type === "deposit"
      ? exchangeStatus.depositAllowed
      : exchangeStatus.withdrawalAllowed;
  return allowed ? "online" : "offline";
};

export const STATUS_LABEL_COLOR = {
  online: "green",
  offline: "red",
  "check failing": "yellow",
  paused: "gray",
  "no data": "gray",
};

export const formatSince = (date) =>
  date ? `${formatDistanceToNowStrict(new Date(date))} ago` : "none recorded";

export const checkFailingSummary = (exchangeStatus) =>
  `Status check failing (${exchangeStatus.checkErrorCount ?? 0} times in a row). Last successful check: ${formatSince(
    exchangeStatus.lastSuccessfulCheckAt,
  )}.${exchangeStatus.lastCheckError ? ` Last error: ${exchangeStatus.lastCheckError}` : ""}`;

export const checksPausedSummary = (exchangeStatus) =>
  `Status check paused ${formatSince(
    exchangeStatus.checksPausedAt
  )}, deposit/withdrawal status is not monitored.`;

// Explains a "check failing" or "paused" status label
export const statusLabelTooltip = (exchangeStatus, label) => {
  if (label === "check failing") return checkFailingSummary(exchangeStatus);
  if (label === "paused") return checksPausedSummary(exchangeStatus);
  return null;
};

// State of the check itself: "paused" | "failing" | "working"
export const checkState = (exchangeStatus) => {
  if (isChecksPaused(exchangeStatus)) return "paused";
  if (isCheckFailing(exchangeStatus)) return "failing";
  return "working";
};

export const CHECK_STATE_COLOR = {
  failing: "yellow",
  paused: "gray",
  working: "green",
};
